import { concat } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import type { Select } from 'ember-power-select/components/power-select';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import { validate } from 'ember-validators';

interface Option {
  value: string;
  disabled?: boolean;
}

interface TagsInputSignature {
  Args: {
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    ariaLabel?: string;
    bgReset?: boolean;
    disabled?: boolean;
    onChange: (selected: string[]) => void;
    placeholder?: string;
    selected?: string[];
    tagLabel?: string | null;
    validationType?: Parameters<typeof validate>[0] | null;
    validationOptions?: Record<string, unknown> | null;
    validationDescription?: string;
  };
  Element: HTMLDivElement;

  Blocks: {
    default: [string];
  };
}

export default class TagsInput extends Component<TagsInputSignature> {
  get tagLabel(): string {
    return this.args.tagLabel ?? 'tag';
  }

  get _selected(): { value: string }[] | undefined {
    return this.args.selected?.map((item) => ({ value: item }));
  }

  @action
  showSearchAsValidatedTagOption(value: string): { value: string; disabled?: boolean }[] {
    if (this.args.validationType) {
      // @ts-expect-error type error
      const validation = validate(this.args.validationType, value, this.args.validationOptions);
      const disabled = validation !== true;
      return [{ value, disabled }];
    } else {
      return [{ value }];
    }
  }

  @action
  handleKeyDown(select: Select, event: KeyboardEvent): boolean | void {
    if (event && (event.key === 'Tab' || event.key === 'Enter') && select.isOpen) {
      event.preventDefault();

      const results = <Option[]>select.results;

      if (results && results[0] && !results[0].disabled) {
        select.actions.choose(results[0], event);
      }

      select.actions.close(event);
      return false;
    }
  }

  @action
  handleChange(options: Option[] = [], select: Select): void {
    const didRemoveAnOption = options.length < (this.args.selected ?? []).length;
    if (didRemoveAnOption) {
      select.actions?.open(); // refocus the TagsInput after removing a tag
    }
    this.args.onChange(options.map((opt) => opt.value));
  }

  <template>
    <div data-test-tags-input>
      <PowerSelectMultiple
        data-test-tags-input-trigger
        @ariaLabel={{@ariaLabel}}
        @ariaLabelledBy={{@ariaLabelledBy}}
        @ariaDescribedBy={{@ariaDescribedBy}}
        @disabled={{@disabled}}
        @dropdownClass="in-modal"
        @triggerClass={{if @bgReset "bg-none"}}
        @selected={{this._selected}}
        @searchMessage={{concat "Add a new " this.tagLabel}}
        @search={{this.showSearchAsValidatedTagOption}}
        @searchEnabled={{true}}
        @searchField="value"
        @placeholder={{@placeholder}}
        @onKeydown={{this.handleKeyDown}}
        @onChange={{this.handleChange}}
        as |option|
      >
        {{! @glint-expect-error type error }}
        {{#if (includes option.value @selected)}}
          {{option.value}}
        {{else if option.disabled}}
          {{#if @validationDescription}}
            "{{option.value}}" is not valid.
            {{@validationDescription}}
          {{else}}
            "{{option.value}}" is not a valid
            {{this.tagLabel}}
          {{/if}}
        {{else}}
          Add "{{option.value}}" as a new
          {{this.tagLabel}}
        {{/if}}
      </PowerSelectMultiple>
    </div>
  </template>
}
